import {useState, useMemo, useEffect} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import back_arrow from "../../assets/backarrow.svg";
import SummaryScoresCard from "../SummaryScoresCard";
import SummaryChart from "../SummaryChart";
import styles from "../../utils/ReactSelectStyles";
import Select from "react-select";
import graphs from "../../assets/graphs_button.svg";
import graphs_filled from "../../assets/graphs_button_filled.svg";
import CoachCommentaryContainer from "../CoachCommentaryContainer";
import pdf_download from "../../assets/download-pdf-temp.png";
import types from "../../assets/constants/types";
import profile_api from "../../api/protected/profile";
import comparison_api from "../../api/protected/comparison";
import SubscriptionNeededPopup from "../../components/SubscriptionNeededPopup";
import GenericDialog from "../GenericDialog";

const SurveySummary = (props) => {
  const surveyData = props.surveyData;
  const comparisonSurveys = props.comparisonSurveys;
  const generatingReport = props.generatingReport;
  const generateReport = props.generateReport;
  const hasStakeholders = props.hasStakeholders;
  const setQuestionsMaps = props.setQuestionsMaps;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [teamMembersEnabled, setTeamMembersEnabled] = useState(true);
  const [teamLeadersEnabled, setTeamLeadersEnabled] = useState(true);
  const [stakeholdersEnabled, setStakeholdersEnabled] = useState(true);
  const [benchmarkEnabled, setBenchmarkEnabled] = useState(true);
  const [categoryBenchmarkEnabled, setCategoryBenchmarkEnabled] = useState(false);

  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
  const [subscription, setSubscription] = useState(null);

  const [hoveringGraphs, setHoveringGraphs] = useState(false);
  const [comparison, setComparison] = useState();
  const [showInsufficientOverlap, setShowInsufficientOverlap] = useState(false);

  const [origin, setOrigin] = useState("origin");

  const dateFormat = (ts) => {
    const d = new Date(ts * 1000);
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
  };

  const handleBackClick = (ev) => {
    if (!ev) return;
    if (window.history.length > 1) {
      navigate(-1);
    } else if (searchParams.get("demo")) {
      navigate(`/surveys/${surveyData.survey_id}?demo=true`);
    } else {
      navigate(`/surveys/${surveyData.survey_id}`);
    }
  };

  const getCard = (card_type) => {
    if (!surveyData) return;
    let i = 0;
    switch (card_type) {
      case "Strengths":
        i = surveyData.summary_aggregates.map(x => x.group_name).indexOf("Team Strengths");
        return surveyData.summary_aggregates[i];
      case "Weaknesses":
        i = surveyData.summary_aggregates.map(x => x.group_name).indexOf("Team Weaknesses");
        return surveyData.summary_aggregates[i];
      case "Team Variance":
        i = surveyData.summary_aggregates.map(x => x.group_name).indexOf("Variance with Team");
        return surveyData.summary_aggregates[i];
      case "Stakeholder Variance":
        i = surveyData.summary_aggregates.map(x => x.group_name).indexOf("Variance with Stakeholder");
        return surveyData.summary_aggregates[i];
      default:
        return null;
    }
  };

  const CompletionBar = (props) => {
    const completion = props.completion;
    const radius = 132;
    const halfWidth = 140;
    const circumference = radius * 2 * Math.PI;
    const dasharray_circ = circumference * 0.905;
    const offset = circumference - (completion / 100) * dasharray_circ || 0;
    const linecap_rotation = (360 * 0.905) * (completion / 100) - 163;

    return (
      <svg className="summary-progress-ring">
        <circle
          className="summary-ring-circle"
          strokeWidth="14"
          stroke="#773dbd"
          fill="transparent"
          r={radius}
          cx={halfWidth}
          cy={halfWidth}
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
        {completion > 0 ? (
          <circle
            className="progress-ring__linecap"
            strokeWidth="3px"
            stroke="#773dbd"
            fill="white"
            r={10}
            cx={halfWidth}
            cy={halfWidth - radius}
            transform={`rotate(${linecap_rotation})`}
          />
        ) : null}
      </svg>
    );
  };

  const handleComparison = () => {
    if (!comparison) return;
    const comparisonPath = `/diagnostic_engagement/${props.surveyId}/${comparison.id}?demo=${props.demo}`;
    comparison_api.getComparisonOverlap(props.surveyId, comparison.id, props.demo)
      .then((res) => {
        if ((res.data.common_participants ?? 0) < 2) {
          setShowInsufficientOverlap(true);
          return;
        }
        navigate(comparisonPath);
      })
      .catch((err) => {
        if (comparison_api.isInsufficientCommonParticipants(err)) {
          setShowInsufficientOverlap(true);
          return;
        }
        navigate(comparisonPath);
      });
  };

  const formatSurveyName = () => {
    return `${surveyData.organisation_name}, ${surveyData.team_name}, ${
      types.point_types.find(x => x.id === surveyData.survey_point).name
    }`;
  };

  // ---------- Build questions maps (513) ----------
  const norm = (s) => (s ?? "").toString().trim().toLowerCase();

  const questionIndexByPillar = useMemo(() => {
    if (!surveyData) return {};
    const out = {};
    for (const pillar of surveyData.pillars || []) {
      const byName = new Map();
      const aqs = pillar.all_question_scores || [];
      for (const q of aqs) {
        const key = norm(q.theme_name);
        if (!byName.has(key)) byName.set(key, []);
        byName.get(key).push(q);
      }
      out[pillar.id] = { byName };
    }
    return out;
  }, [surveyData]);

  const attachQuestions = (pillarId, theme) => {
    const key = norm(theme.name);
    const qs = questionIndexByPillar[pillarId]?.byName?.get(key) || [];
    return { ...theme, questions: qs };
  };

  const getTop5Group = (name) =>
    surveyData?.summary_aggregates?.find(x => x.group_name === name) || null;

  const computedMaps = useMemo(() => {
    if (!surveyData) return null;

    const chartMap = (surveyData.pillars || []).map((pillar) => {
      const themes = pillar.themes || [];
      return themes.map((theme) => attachQuestions(pillar.id, theme));
    });

    const groupThemes = (name) => {
      const group = getTop5Group(name);
      return (group?.themes || []).map((t) => attachQuestions(t.pillar_id, t));
    };

    const top5Map = {
      0: groupThemes("Team Strengths"),
      1: groupThemes("Team Weaknesses"),
      2: groupThemes("Variance with Team"),
      3: groupThemes("Variance with Stakeholder"),
    };

    return { chartMap, top5Map };
  }, [surveyData, questionIndexByPillar]);

  useEffect(() => {
    if (setQuestionsMaps && computedMaps) {
      setQuestionsMaps(computedMaps);
    }
  }, [computedMaps, setQuestionsMaps]);

  const handlePdfClick = (ev) => {
    // subscription check disabled for now
    // if (!subscription || subscription < 1) return setShowSubscriptionPopup(true);
    generateReport(ev);
  };

  const getSubscription = () => {
    let response = profile_api.getSubscription();
    response.then(response => {
      setSubscription(response.data.subscription.type);
    }).catch(() => {});
  };

  useEffect(() => {
    getSubscription();
  }, []);

  return (
    <>
      <div className="summary-header-container" style={{ margin: generatingReport ? "0 30px" : null }}>
        <div className="summary-title-container">
          <div
            className="summary-back-button link"
            onClick={handleBackClick}
            style={{ display: generatingReport ? "none" : null }}
          >
            <img className={"back-arrow-classname"} src={back_arrow} alt="Back Arrow" />
          </div>
          <div className="summary-title">PERILL Diagnostics for Teams: Results</div>
        </div>
        {comparisonSurveys && !generatingReport ? (
          <div className="summary-comparison-card-container">
            <div className="summary-comparison-card-title">Compare Summaries:</div>
            <div className="summary-comparison-body-container">
              <div className="summary-comparison-dropdown-container">
                <Select
                  name="traits.organisation_role"
                  options={comparisonSurveys}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id}
                  value={comparison}
                  placeholder=""
                  onChange={(ev) => setComparison(ev)}
                  styles={styles.registerStyles}
                  required={false}
                />
              </div>
              <div
                className={
                  comparison ? "summary-comparison-search-container" : "summary-comparison-search-container disabled"
                }
                onClick={handleComparison}
              >
                <img
                  src={hoveringGraphs ? graphs_filled : graphs}
                  alt={"search"}
                  onMouseOver={() => setHoveringGraphs(true)}
                  onMouseLeave={() => setHoveringGraphs(false)}
                />
              </div>
              <div className="pdf-download-button-container" onClick={handlePdfClick}>
                <img src={pdf_download} alt="Download PDF" />
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div className="survey-summary-details-container" style={{ display: props.hideDetails ? "none" : null }}>
        <div className="survey-summary-details-subcontainer left" style={{ padding: generatingReport ? "30px 0 30px 30px" : null }}>
          <div className="survey-summary-details-heading-text">Organisation:</div>
          <div className="survey-summary-details-value-text">{surveyData && surveyData.organisation_name}</div>
          <div className="survey-summary-details-heading-text">Team:</div>
          <div className="survey-summary-details-value-text">{surveyData && surveyData.team_name}</div>
          <div className="survey-summary-details-heading-text">Survey Type</div>
          <div className="survey-summary-details-light-switch-container">
            <div className="survey-summary-details-light-switch">
              <div className={`light-switch ${surveyData && surveyData.survey_type !== 1 ? "active" : "inactive"}`} />
              <div className="light-switch-text">Lite</div>
            </div>
            <div className="survey-summary-details-light-switch">
              <div className={`light-switch ${surveyData && surveyData.survey_type === 1 ? "active" : "inactive"}`} />
              <div className="light-switch-text">Full</div>
            </div>
          </div>
          <div className="survey-summary-details-heading-text">Survey Point</div>
          <div className="survey-summary-details-light-switch-container">
            <div className="survey-summary-details-light-switch">
              <div className={`light-switch ${surveyData && surveyData.survey_point === 1 ? "active" : "inactive"}`} />
              <div className="light-switch-text">Initial</div>
            </div>
            <div className="survey-summary-details-light-switch">
              <div className={`light-switch ${surveyData && surveyData.survey_point === 2 ? "active" : "inactive"}`} />
              <div className="light-switch-text">Midpoint</div>
            </div>
            <div className="survey-summary-details-light-switch">
              <div className={`light-switch ${surveyData && surveyData.survey_point === 3 ? "active" : "inactive"}`} />
              <div className="light-switch-text">Endpoint</div>
            </div>
            <div className="survey-summary-details-light-switch">
              <div className="light-switch-text">Post Engagement</div>
              <div className={`light-switch ${surveyData && surveyData.survey_point === 4 ? "active" : "inactive"}`} />
            </div>
          </div>
        </div>
        <div className="survey-summary-details-container-divider" />
        <div className="survey-summary-details-subcontainer right" style={{ padding: generatingReport ? "30px" : null }}>
          <div className="completion-circle" style={{ zIndex: generatingReport ? 1 : null }}>
            <div className="survey-summary-details-completion-circle-container">
              <div className="survey-summary-details-completion-circle">
                <div className="survey-summary-details-completion-circle-inner-ring">
                  <div className="survey-summary-details-completion-circle-inner">
                    <div className="survey-summary-completion-circle-text-container">
                      <div className="survey-summary-details-completion-circle-percentage-text">
                        {`${(surveyData && surveyData.survey_percent_complete) || 0}%`}
                      </div>
                      <div className="survey-summary-details-completion-circle-percentage-subtext">COMPLETE</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <CompletionBar completion={surveyData && surveyData.survey_percent_complete} />
          </div>
          <div className="survey-summary-details-completion-text-container">
            <div className="survey-summary-details-heading-text">Survey Name:</div>
            <div className="survey-summary-details-value-text">{surveyData && formatSurveyName()}</div>
            <div className="survey-summary-details-heading-text">Number of participants</div>
            <div className="survey-summary-details-value-text">{surveyData && surveyData.participant_count}</div>
            <div className="survey-summary-details-heading-text">Created</div>
            <div className="survey-summary-details-value-text">
              {searchParams.get("demo") ? "N/A" : dateFormat(surveyData && surveyData.survey_created_date)}
            </div>
            <div className="survey-summary-details-heading-text">Expires</div>
            <div className="survey-summary-details-value-text">
              {searchParams.get("demo") ? "N/A" : dateFormat(surveyData && surveyData.survey_expiry_date)}
            </div>
          </div>
        </div>
      </div>

      <CoachCommentaryContainer
        context_id={props.surveyId}
        section_id={`summary-overview`}
        generatingReport={generatingReport}
        demo={props.demo}
        subscription={subscription}
      />
      <div className="survey-summary-chart-container" id="summary-pdf-block" style={{ padding: generatingReport ? "30px" : null, display: props.hideChart ? "none" : null }}>
        <div className="summary-chart-details">
          <div className="summary-chart-details-title">PERILL Results Wheel</div>
          <div className={`summary-chart-details-controls-container${surveyData?.category_name ? " has-category" : ""}`}>
            <div className="summary-chart-details-controls-row">
              <div className="chart-controls-circle green"></div>
              <div className="chart-controls-text">Team Members</div>
              <div
                className={`chart-controls-switch ${teamMembersEnabled ? "active" : "inactive"}`}
                onClick={() => setTeamMembersEnabled(!teamMembersEnabled)}
                style={{ opacity: generatingReport ? 0 : null }}
              >
                <div className="chart-controls-switch-centre" />
              </div>
            </div>
            <div className="summary-chart-details-controls-row">
              <div className="chart-controls-circle pink"></div>
              <div className="chart-controls-text">Team Leader(s)</div>
              <div
                className={`chart-controls-switch ${teamLeadersEnabled ? "active" : "inactive"}`}
                onClick={() => setTeamLeadersEnabled(!teamLeadersEnabled)}
                style={{ opacity: generatingReport ? 0 : null }}
              >
                <div className="chart-controls-switch-centre" />
              </div>
            </div>
            <div className="summary-chart-details-controls-row">
              <div className="chart-controls-circle blue"></div>
              <div className="chart-controls-text">Stakeholder(s)</div>
              <div
                className={`chart-controls-switch ${stakeholdersEnabled ? "active" : "inactive"}`}
                onClick={() => setStakeholdersEnabled(!stakeholdersEnabled)}
                style={{ opacity: generatingReport ? 0 : null }}
              >
                <div className="chart-controls-switch-centre" />
              </div>
            </div>
            <div className="summary-chart-details-controls-row">
              <div className="chart-controls-circle grey"></div>
              <div className="chart-controls-text">Benchmark</div>
              <div
                className={`chart-controls-switch ${benchmarkEnabled ? "active" : "inactive"}`}
                onClick={() => setBenchmarkEnabled(!benchmarkEnabled)}
                style={{ opacity: generatingReport ? 0 : null }}
              >
                <div className="chart-controls-switch-centre" />
              </div>
            </div>
            {surveyData?.category_name ?
              <div className="summary-chart-details-controls-row">
                <div className="chart-controls-circle teal"></div>
                <div className="chart-controls-text">{surveyData.category_name} Benchmark</div>
                <div
                  className={`chart-controls-switch ${categoryBenchmarkEnabled ? "active" : "inactive"}`}
                  onClick={() => setCategoryBenchmarkEnabled(!categoryBenchmarkEnabled)}
                  style={{ opacity: generatingReport ? 0 : null }}
                >
                  <div className="chart-controls-switch-centre" />
                </div>
              </div> : null
            }
          </div>
          <div className="summary-chart-details-subtext">
            This polar graph shows the mean scores for your participant groups, with scores ranging from 1-50. Each
            section of the graph represents a theme within the PERILL pillars. The score for each group in each theme is
            represented by a coloured dot, and the further out the dot from the centre, the higher the score.
            <br />
            <br />
            {!generatingReport
              ? "Toggle on or off the participant groups or click on each dot to see the score for that group."
              : null}
          </div>
        </div>

        <div className="summary-chart-container">
          <div className="summary-interactive-chart">
            <SummaryChart
              surveyData={surveyData}
              tmEnabled={teamMembersEnabled}
              tlEnabled={teamLeadersEnabled}
              // Only enable ST if they actually exist (654 behavior)
              stEnabled={hasStakeholders && stakeholdersEnabled}
              bmEnabled={benchmarkEnabled}
              categoryBmEnabled={Boolean(surveyData?.category_name) && categoryBenchmarkEnabled}
              // Keep 513's origin-aware navigation for Pillars
              showPillars={(i) => {
                setOrigin("chart");
                props.showPillars(i, "chart");
              }}
            />
          </div>
        </div>
      </div>

      {!props.hideChart ? (
        <CoachCommentaryContainer
          context_id={props.surveyId}
          section_id={`summary-radial-chart`}
          generatingReport={generatingReport}
          demo={props.demo}
          subscription={subscription}
        />
      ) : null}

      <div className="pdf-spacer" style={{ height: 200, display: generatingReport ? "block" : "none" }} />

        <div
          className="survey-summary-collective-scores"
          style={{
            padding: generatingReport ? "30px" : null,
            height: generatingReport ? "unset" : null,
            display: props.hideCollective ? "none" : null,
          }}
        >
        <div className="summary-chart-details-title">Diagnostic Results Highlights</div>
        <div className="summary-collective-scores-card-container">
          <div className="summary-collective-scores-card-row">
            <SummaryScoresCard
              title="Strengths"
              card={getCard("Strengths")}
              showTop5s={(t) => {
                setOrigin("top5s");
                props.showTop5s(t, "top5s");
              }}
              hasStakeholders={hasStakeholders}
              generatingReport={generatingReport}
            />
            <SummaryScoresCard
              title="Weaknesses"
              card={getCard("Weaknesses")}
              showTop5s={(t) => {
                setOrigin("top5s");
                props.showTop5s(t, "top5s");
              }}
              hasStakeholders={hasStakeholders}
              generatingReport={generatingReport}
            />
          </div>
          <div className="summary-collective-scores-card-row">
            <SummaryScoresCard
              title="Team Variance"
              card={getCard("Team Variance")}
              showTop5s={(t) => {
                setOrigin("top5s");
                props.showTop5s(t, "top5s");
              }}
              generatingReport={generatingReport}
            />
            <SummaryScoresCard
              title="Stakeholder Variance"
              card={getCard("Stakeholder Variance")}
              showTop5s={(t) => {
                setOrigin("top5s");
                props.showTop5s(t, "top5s");
              }}
              hasStakeholders={hasStakeholders}
              generatingReport={generatingReport}
            />
          </div>
        </div>
      </div>

      <CoachCommentaryContainer
        context_id={props.surveyId}
        section_id={`summary-collective-scores`}
        generatingReport={generatingReport}
        demo={props.demo}
        subscription={subscription}
      />
      {showSubscriptionPopup && (
          <SubscriptionNeededPopup
              onClose={() => setShowSubscriptionPopup(false)}
          />
      )}
      {showInsufficientOverlap && (
        <GenericDialog
          title="Comparison Summary"
          text={comparison_api.INSUFFICIENT_COMMON_PARTICIPANTS_MESSAGE}
          buttons={[{"text": "OK", "id": "1"}]}
          result={() => setShowInsufficientOverlap(false)}
        />
      )}
    </>
  );
};

export default SurveySummary;
